import React, { useMemo, useState,useEffect,act } from 'react';
import '../../CSS/OperatorProductivityReport.css';

// Adjust this import path to match where this file sits relative to src/api.js
// in your project (see how other Reports/* components import it).
import api from '../../api';

// Cycled per-operator avatar color — the API doesn't return a color,
// so we assign one client-side based on row position.
const PALETTE = ['#2E6E9E', '#B0761A', '#2E7D5B', '#7A5AA8', '#C24C4C', '#3A4F66', '#1E8A8A', '#B5548C'];
//
function iso(d) {
  return d.toISOString().slice(0, 10);
}
function fmt(n) {
  return n.toLocaleString('en-IN');
}
function prettyDate(s) {
  const [y, m, dd] = s.split('-');
  const mon = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][+m - 1];
  return `${dd} ${mon} ${y}`;
}

function exportCSV(rows, fromISO, toISO) {
  const head = ['Rank', 'Operator ID', 'Operator', 'Consignments Booked', 'Bills Generated', 'Bills per Booking', 'Share %'];
  const lines = [head.join(',')];
  rows.forEach((r, i) => {
    lines.push([i + 1, r.id, `"${r.name}"`, r.cons, r.bills, r.ratio.toFixed(2), (r.share * 100).toFixed(1)].join(','));
  });
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `operator-productivity_${fromISO}_${toISO}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function OperatorProductivityReport() {
  const today = useMemo(() => new Date(), []);
  const defaultFrom = useMemo(() => iso(new Date(today.getFullYear(), today.getMonth(), 1)), [today]);
  const defaultTo = useMemo(() => iso(today), [today]);

  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);
  const [query, setQuery] = useState('');
  const [sortKey, setSortKey] = useState('cons');
  const [sortDir, setSortDir] = useState(-1);
  const [preset, setPreset] = useState('month');

  const [totalsAll, setTotalsAll] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [genAt, setGenAt] = useState('');

  // Normalise / swap range if needed
  const [fromISO, toISO] = from <= to ? [from, to] : [to, from];

  // ── LOAD PRODUCTIVITY DATA — single dedicated endpoint (API 2) ──
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);

    api
      .get('/get-operator-productivity', { params: { fromDate: fromISO, toDate: toISO } })
      .then((res) => {
        if (cancelled) return;
        const rows = res.data?.data || [];
        const data = rows.map((r, i) => ({
          id: r.empCode || r.EmpCode || `OP-${r.user_id ?? r.User_id}`,
          name: r.user_name || r.User_name || '—',
          color: PALETTE[i % PALETTE.length],
          cons: r.consignments ?? r.Consignments ?? 0,
          bills: r.bills ?? r.Bills ?? 0,
        }));
        setTotalsAll(data);
        const d = new Date();
        setGenAt(
          'Generated ' +
            d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) +
            ' ' +
            d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
        );
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(err.response?.data?.message || 'Failed to load operator productivity data');
        setTotalsAll([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [fromISO, toISO]);

  const grandCons = useMemo(() => totalsAll.reduce((s, r) => s + r.cons, 0), [totalsAll]);
  const grandBills = useMemo(() => totalsAll.reduce((s, r) => s + r.bills, 0), [totalsAll]);
  const activeAll = useMemo(() => totalsAll.filter((r) => r.cons > 0).length, [totalsAll]);

  const rows = useMemo(() => {
    let list = totalsAll
      .filter((r) => r.cons > 0 || r.bills > 0)
      .map((r) => ({
        ...r,
        ratio: r.cons ? r.bills / r.cons : 0,
        share: grandCons ? r.cons / grandCons : 0,
      }));

    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter((r) => r.name.toLowerCase().includes(q) || r.id.toLowerCase().includes(q));
    }

    list.sort((a, b) => (a[sortKey] - b[sortKey]) * sortDir || a.name.localeCompare(b.name));
    return list;
  }, [totalsAll, grandCons, query, sortKey, sortDir]);

  const maxCons = Math.max(1, ...rows.map((r) => r.cons));
  const maxBills = Math.max(1, ...rows.map((r) => r.bills));

  const fCons = rows.reduce((s, r) => s + r.cons, 0);
  const fBills = rows.reduce((s, r) => s + r.bills, 0);
  const isFiltered = query.trim().length > 0;

  const kConsSub = `${activeAll} operators · ${prettyDate(fromISO)}–${prettyDate(toISO)}`.replace(/ \d{4}/g, '');
  const kBillsSub = `${grandCons ? Math.round((grandBills / grandCons) * 100) : 0}% of bookings billed`;

  function applyPreset(kind) {
    setPreset(kind);
    if (kind === 'month') {
      setFrom(iso(new Date(today.getFullYear(), today.getMonth(), 1)));
      setTo(iso(today));
    } else {
      const days = +kind.split(':')[1];
      const f = new Date(today);
      f.setDate(f.getDate() - days);
      setFrom(iso(f));
      setTo(iso(today));
    }
  }

  function handleSort(key) {
    if (sortKey === key) {
      setSortDir((d) => d * -1);
    } else {
      setSortKey(key);
      setSortDir(-1);
    }
  }

  const columns = [
    { key: 'cons', label: 'Consignments' },
    { key: 'bills', label: 'Bills' },
    { key: 'ratio', label: 'Bills / Booking' },
    { key: 'share', label: 'Share' },
  ];

  return (
    <div className="opr-report">
      <div className="shell">
        <div className="rpt-head">
          <div>
            <div className="eyebrow">Operator Productivity</div>
            <h1>Consignments &amp; Bills by Operator</h1>
            <p>Bookings entered and bills generated by each data-entry operator over the selected period.</p>
          </div>
          <div className="stamp">
            <div className="lbl">Reporting Period</div>
            <div className="rng">
              {prettyDate(fromISO)} – {prettyDate(toISO)}
            </div>
          </div>
        </div>

        <div className="controls">
          <div className="field">
            <label htmlFor="from">From</label>
            <input
              type="date"
              id="from"
              value={from}
              onChange={(e) => {
                setPreset(null);
                setFrom(e.target.value);
              }}
            />
          </div>
          <div className="field">
            <label htmlFor="to">To</label>
            <input
              type="date"
              id="to"
              value={to}
              onChange={(e) => {
                setPreset(null);
                setTo(e.target.value);
              }}
            />
          </div>
          <div className="field">
            <label>Quick range</label>
            <div className="presets">
              <button className={`chip${preset === 'days:0' ? ' on' : ''}`} onClick={() => applyPreset('days:0')}>
                Today
              </button>
              <button className={`chip${preset === 'days:6' ? ' on' : ''}`} onClick={() => applyPreset('days:6')}>
                7 days
              </button>
              <button className={`chip${preset === 'days:29' ? ' on' : ''}`} onClick={() => applyPreset('days:29')}>
                30 days
              </button>
              <button className={`chip${preset === 'month' ? ' on' : ''}`} onClick={() => applyPreset('month')}>
                This month
              </button>
            </div>
          </div>
          <div className="field">
            <label htmlFor="q">Find operator</label>
            <input
              type="search"
              id="q"
              placeholder="Name or code…"
              autoComplete="off"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div className="ctrl-spacer"></div>
          <button className="btn solid" onClick={() => exportCSV(rows, fromISO, toISO)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Export CSV
          </button>
        </div>

        <div className="kpis">
          <div className="kpi">
            <div className="k-lbl">Consignments Booked</div>
            <div className="k-val num">{fmt(grandCons)}</div>
            <div className="k-sub">{kConsSub}</div>
          </div>
          <div className="kpi">
            <div className="k-lbl">Bills Generated</div>
            <div className="k-val num">{fmt(grandBills)}</div>
            <div className="k-sub">{kBillsSub}</div>
          </div>
          <div className="kpi">
            <div className="k-lbl">Active Operators</div>
            <div className="k-val num">{activeAll}</div>
            <div className="k-sub">with entries in period</div>
          </div>
          <div className="kpi">
            <div className="k-lbl">Bills / Booking</div>
            <div className="k-val num">{grandCons ? (grandBills / grandCons).toFixed(2) : '0.00'}</div>
            <div className="k-sub">avg. billing coverage</div>
          </div>
        </div>

        <div className="table-wrap">
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th className="rank">#</th>
                  <th>Operator</th>
                  {columns.map((c) => (
                    <th
                      key={c.key}
                      className={`r sortable${sortKey === c.key ? ' act' : ''}`}
                      onClick={() => handleSort(c.key)}
                    >
                      {c.label} <span className="arr">{sortKey === c.key ? (sortDir < 0 ? '▼' : '▲') : '▼'}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6}>
                      <div className="empty">
                        <h3>Loading…</h3>
                        <p>Fetching productivity data for the selected period.</p>
                      </div>
                    </td>
                  </tr>
                ) : loadError ? (
                  <tr>
                    <td colSpan={6}>
                      <div className="empty">
                        <h3>Couldn't load data</h3>
                        <p>{loadError}</p>
                      </div>
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={6}>
                      <div className="empty">
                        <h3>No entries in this period</h3>
                        <p>Try widening the date range or clearing the operator filter.</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  rows.map((r, idx) => {
                    const initials = r.name
                      .split(' ')
                      .map((w) => w[0])
                      .slice(0, 2)
                      .join('');
                    return (
                      <tr key={r.id}>
                        <td className="rank">{String(idx + 1).padStart(2, '0')}</td>
                        <td>
                          <div className="op">
                            <div className="av" style={{ background: r.color }}>
                              {initials}
                            </div>
                            <div>
                              <div className="op-name">{r.name}</div>
                              <div className="op-id">{r.id}</div>
                            </div>
                          </div>
                        </td>
                        <td className="r">
                          <div className="metric">
                            <span className="n">{fmt(r.cons)}</span>
                            <div className="bar b-cons">
                              <i style={{ width: `${((r.cons / maxCons) * 100).toFixed(1)}%` }}></i>
                            </div>
                          </div>
                        </td>
                        <td className="r">
                          <div className="metric">
                            <span className="n">{fmt(r.bills)}</span>
                            <div className="bar b-bill">
                              <i style={{ width: `${((r.bills / maxBills) * 100).toFixed(1)}%` }}></i>
                            </div>
                          </div>
                        </td>
                        <td className="r">
                          <span className="ratio">{r.ratio.toFixed(2)}</span>
                        </td>
                        <td className="r">
                          <span className="share">{(r.share * 100).toFixed(1)}%</span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
              {rows.length > 0 && (
                <tfoot>
                  <tr>
                    <td></td>
                    <td>Total{isFiltered ? ` (filtered · ${rows.length})` : ` · ${rows.length} operators`}</td>
                    <td className="r">
                      <span className="num">{fmt(fCons)}</span>
                    </td>
                    <td className="r">
                      <span className="num">{fmt(fBills)}</span>
                    </td>
                    <td className="r">
                      <span className="num">{fCons ? (fBills / fCons).toFixed(2) : '0.00'}</span>
                    </td>
                    <td className="r">
                      <span className="num">{isFiltered && grandCons ? `${((fCons / grandCons) * 100).toFixed(1)}%` : '100.0%'}</span>
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>

        <div className="foot-note">
          <span>Counts reflect entries dated within the selected period. Click a column heading to sort.</span>
          <span>{genAt}</span>
        </div>
      </div>
    </div>
  );
}
